import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiEdit2,
  FiTrash2,
  FiLock,
  FiCode,
  FiSliders,
  FiHash,
  FiToggleLeft,
  FiList,
  FiType,
  FiLink
} from 'react-icons/fi';
import { ArchitectureVariable, VariableType } from '../../api/variables';

interface VariableCardProps {
  variable: ArchitectureVariable;
  onEdit: () => void;
  onDelete: () => void;
}

const VariableCard: React.FC<VariableCardProps> = ({ variable, onEdit, onDelete }) => {
  const { t } = useTranslation('canvas');

  // Get icon for variable type
  const getTypeIcon = (type: VariableType) => {
    switch (type) {
      case 'string':
        return <FiType size={14} />;
      case 'number':
        return <FiHash size={14} />;
      case 'boolean':
        return <FiToggleLeft size={14} />;
      case 'select':
      case 'multiselect':
        return <FiList size={14} />;
      case 'json':
        return <FiCode size={14} />;
      case 'reference':
        return <FiLink size={14} />;
      default:
        return <FiSliders size={14} />;
    }
  };

  // Get color for scope badge
  const getScopeColor = (scope: string) => {
    return scope === 'parameter'
      ? { bg: '#DBEAFE', text: '#1D4ED8', border: '#93C5FD' }
      : { bg: '#F3E8FF', text: '#7C3AED', border: '#C4B5FD' };
  };

  const scopeColor = getScopeColor(variable.scope);

  // Format default value for display
  const formatDefaultValue = () => {
    if (!variable.default_value) return '-';

    if (variable.default_value.expression) {
      return (
        <code style={{
          fontSize: 11,
          background: '#F3F4F6',
          padding: '2px 6px',
          borderRadius: 4,
          color: '#7C3AED',
        }}>
          {variable.default_value.expression}
        </code>
      );
    }

    const value = variable.default_value.value;
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Get validation summary
  const getValidationSummary = () => {
    const rules = variable.validation_rules;
    if (!rules) return null;

    const parts: string[] = [];
    if (rules.required) parts.push(t('variables.validation.required', 'required'));
    if (rules.min !== undefined) parts.push(`min: ${rules.min}`);
    if (rules.max !== undefined) parts.push(`max: ${rules.max}`);
    if (rules.minLength !== undefined) parts.push(`minLen: ${rules.minLength}`);
    if (rules.maxLength !== undefined) parts.push(`maxLen: ${rules.maxLength}`);
    if (rules.pattern) parts.push('pattern');
    if (rules.enum?.length) parts.push(`${rules.enum.length} options`);

    return parts.length > 0 ? parts.join(', ') : null;
  };

  const validationSummary = getValidationSummary();

  return (
    <div
      style={{
        padding: 12,
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#6366F1';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#E5E7EB';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {/* Scope badge */}
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                background: scopeColor.bg,
                color: scopeColor.text,
                border: `1px solid ${scopeColor.border}`,
                borderRadius: 4,
                textTransform: 'uppercase',
              }}
            >
              {variable.scope === 'parameter' ? t('variables.param', 'param') : t('variables.var', 'var')}
            </span>

            {/* Type badge */}
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                padding: '2px 6px',
                background: '#F3F4F6',
                color: '#6B7280',
                borderRadius: 4,
              }}
            >
              {getTypeIcon(variable.type)}
              {variable.type}
            </span>

            {/* Sensitive badge */}
            {variable.is_sensitive && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 11,
                  padding: '2px 6px',
                  background: '#FEF3C7',
                  color: '#B45309',
                  borderRadius: 4,
                }}
              >
                <FiLock size={12} />
                {t('variables.sensitive', 'sensitive')}
              </span>
            )}
          </div>

          {/* Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <code style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#111827',
              background: '#F9FAFB',
              padding: '2px 6px',
              borderRadius: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 180,
            }}>
              {variable.name}
            </code>
            {variable.display_name && (
              <span style={{ fontSize: 13, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                ({variable.display_name})
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            style={{
              padding: 6,
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#F3F4F6';
              e.currentTarget.style.borderColor = '#D1D5DB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }}
            title={t('variables.edit', 'Edit')}
          >
            <FiEdit2 size={14} style={{ color: '#6B7280' }} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              padding: 6,
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#FEE2E2';
              e.currentTarget.style.borderColor = '#FECACA';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }}
            title={t('variables.delete', 'Delete')}
          >
            <FiTrash2 size={14} style={{ color: '#EF4444' }} />
          </button>
        </div>
      </div>

      {/* Description */}
      {variable.description && (
        <p style={{
          fontSize: 12,
          color: '#6B7280',
          margin: '8px 0',
          lineHeight: 1.5,
        }}>
          {variable.description}
        </p>
      )}

      {/* Default value */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: '#6B7280',
        marginTop: 8,
        paddingTop: 8,
        borderTop: '1px solid #F3F4F6',
      }}>
        <span style={{ fontWeight: 500 }}>{t('variables.default', 'Default')}:</span>
        <span style={{ color: '#374151' }}>{formatDefaultValue()}</span>
      </div>

      {/* Validation summary */}
      {validationSummary && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 11,
          color: '#9CA3AF',
          marginTop: 6,
        }}>
          <span>{t('variables.rules', 'Rules')}:</span>
          <span>{validationSummary}</span>
        </div>
      )}
    </div>
  );
};

export default VariableCard;
